import { forwardRef } from 'react';
import {
  KeyboardAwareScrollView,
  type KeyboardAwareScrollViewProps,
  type KeyboardAwareScrollViewRef,
} from 'react-native-keyboard-controller';

/**
 * Gap (px) kept between the focused field and the top of the keyboard so the
 * field never sits flush against it. Small enough to avoid a dead band, large
 * enough to read as intentional breathing room.
 */
export const KEYBOARD_CLEARANCE = 24;

export type KeyboardAwareScreenScrollProps = KeyboardAwareScrollViewProps;

/**
 * The single keyboard-aware scroll container for form screens. Replaces the
 * hand-rolled `KeyboardAvoidingView` + `ScrollView` pairs each form used to
 * duplicate -- there are no per-screen `keyboardVerticalOffset` values or
 * Android special-cases to tune, because `react-native-keyboard-controller`
 * tracks the keyboard identically on iOS and Android. On web it renders a plain
 * scroll view (the browser already scrolls focused inputs into view), so the
 * same component is correct on all three platforms.
 */
export const KeyboardAwareScreenScroll = forwardRef<
  KeyboardAwareScrollViewRef,
  KeyboardAwareScreenScrollProps
>(function KeyboardAwareScreenScroll(
  {
    bottomOffset = KEYBOARD_CLEARANCE,
    keyboardShouldPersistTaps = 'handled',
    showsVerticalScrollIndicator = false,
    children,
    ...rest
  },
  ref
) {
  return (
    <KeyboardAwareScrollView
      ref={ref}
      bottomOffset={bottomOffset}
      keyboardShouldPersistTaps={keyboardShouldPersistTaps}
      showsVerticalScrollIndicator={showsVerticalScrollIndicator}
      {...rest}
    >
      {children}
    </KeyboardAwareScrollView>
  );
});

/**
 * `renderScrollComponent` for a `FlatList`/`SectionList` so an inline `TextInput`
 * inside a list row (e.g. the comment composer inside a feed card) scrolls above
 * the keyboard on BOTH platforms. RN's `automaticallyAdjustKeyboardInsets` is
 * iOS-only, so a plain list stays put on Android; this swaps the list's scroll
 * container for the keyboard-aware one instead.
 */
export function renderKeyboardAwareScroll(props: KeyboardAwareScreenScrollProps) {
  return <KeyboardAwareScreenScroll {...props} />;
}
